import { NEW_CHAT_ID } from "@/constants";
import { useHandler } from "@/hooks";
import { usePrevious } from "@/hooks/usePrevious";
import { useActiveProjectId } from "@/lib/hooks/useActiveProjectId";
import { useIsMessageGenerating } from "@/lib/hooks/useIsMessageGenerating";
import { useSendProjectMessage } from "@/lib/hooks/useSendProjectMessage";
import {
  type Prompt,
  useInputStore,
  useMessageStore,
  useQueuedPromptsStore,
} from "@/state/stores";
import { createStore, del, get, set, update } from "idb-keyval";
import { nanoid } from "nanoid";
import { useEffect, useRef } from "react";

const DEBOUNCE_DELAY = 300;

const promptDraftsStore = createStore("prompt-drafts", "keyval");
const promptQueueStore = createStore("prompt-queue", "keyval");

type Message = Prompt["message"];
type PersistedPromptsOptions = { processQueue?: boolean };

/**
 * Manages message drafts and prompt queues with IndexedDB persistence.
 * Provides auto-save drafts, queued prompt management, and sequential
 * processing.
 * Uses IndexedDB-first updates for consistency, with only one processQueue
 * instance.
 *
 * @param options - Configuration with processQueue flag to enable queue
 * processing
 * @returns Functions to manage persisted prompts: set/clear drafts and
 * add/remove from queue
 */
export function usePersistedPrompts({
  processQueue,
}: PersistedPromptsOptions = {}) {
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const hasMessage = useInputStore(({ message }) => message.length > 0);
  const setMessage = useInputStore(({ setMessage }) => setMessage);

  const setPrompts = useQueuedPromptsStore((state) => state.setPrompts);
  const firstPrompt = useQueuedPromptsStore((state) => state.prompts.at(0));
  const addToPrompts = useQueuedPromptsStore((state) => state.addToPrompts);
  const removeFromPrompts = useQueuedPromptsStore((s) => s.removeFromPrompts);
  const isAtQueueLimit = useQueuedPromptsStore((s) => s.isAtQueueLimit);

  const projectId = useActiveProjectId();
  const prevProjectId = usePrevious(projectId);

  const sendMessage = useSendProjectMessage();
  const isMessageGenerating = useIsMessageGenerating();

  // Prevents duplicate processing - tracks last processed prompt ID
  const lastProcessedIdRef = useRef<string | null>(null);
  // Concurrency guard to prevent overlapping sends while state flips
  const isProcessingQueuedRef = useRef<boolean>(false);

  // Wait for initial chat load to complete on refresh
  const isChatLoading = useMessageStore((s) => s.isLoading);

  // State transition tracking for queue processing timing
  const prevFirstPromptId = usePrevious(firstPrompt?.id);
  const prevIsGenerating = usePrevious(isMessageGenerating);

  // Storage key - isolated per project, fallback for new projects
  const draftId = projectId ?? NEW_CHAT_ID;

  // Load the persisted message when the component mounts or when the project ID
  // changes. Only gets triggered where the process queue is enabled.
  useEffect(() => {
    if (!processQueue) {
      return;
    }

    // Detect project switches to clear input before loading new project's draft
    const isProjectSwitch = projectId !== prevProjectId;

    // Only load persisted message if current message is empty to prevent
    // overwriting user input during race conditions
    if (!hasMessage || isProjectSwitch) {
      get<Message>(draftId, promptDraftsStore)
        .then((persistedMessage) => setMessage(persistedMessage ?? ""))
        .catch((error) =>
          console.warn("Failed to load persisted message:", error),
        );
    }

    get<Prompt[]>(draftId, promptQueueStore)
      .then((prompts) => setPrompts(prompts ?? []))
      .catch((error) =>
        console.warn("Failed to load persisted prompts:", error),
      );

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = undefined;
      }
    };
  }, [
    draftId,
    processQueue,
    setMessage,
    hasMessage,
    setPrompts,
    projectId,
    prevProjectId,
  ]);

  /**
   * Clears the persisted message of the current project from IndexedDB.
   */
  const clearPersistedPrompt = useHandler(() => {
    del(draftId, promptDraftsStore).catch((error) => {
      console.warn("Failed to clear persisted message:", error);
    });
  });

  /**
   * Sets the persisted message of the current project to IndexedDB.
   * Clears the message if it's empty.
   * The calls are debounced to avoid excessive IndexedDB writes during typing
   */
  const setPersistedPrompt = useHandler((message: Message) => {
    clearTimeout(debounceTimeoutRef.current);

    const trimmedMessage = message.trim();
    if (!trimmedMessage) {
      clearPersistedPrompt();
      return;
    }

    // Debounce the IndexedDB write to avoid excessive writes during typing
    debounceTimeoutRef.current = setTimeout(() => {
      set(draftId, message, promptDraftsStore).catch((error) => {
        console.warn("Failed to save persisted message:", error);
      });
    }, DEBOUNCE_DELAY);
  });

  const addToPromptQueue = useHandler(async (message: Message) => {
    // Early return if queue is at limit
    if (isAtQueueLimit) {
      return;
    }

    const prompt: Prompt = {
      id: nanoid(),
      message,
    };

    try {
      // Update IndexedDB first to ensure persistence consistency
      await update<Prompt[] | undefined>(
        draftId,
        (promptQueue) => (promptQueue || []).concat(prompt),
        promptQueueStore,
      );

      // Only update Zustand store after successful IndexedDB update
      addToPrompts(prompt);

      // Clear persisted draft when queueing to prevent stale drafts on reload
      clearPersistedPrompt();
    } catch (error) {
      console.warn("Failed to add prompt to queue:", error);
      // Don't update Zustand store if IndexedDB update failed
    }
  });

  const removeFromPromptQueue = useHandler(async (promptId: Prompt["id"]) => {
    try {
      // Update IndexedDB first to ensure persistence consistency
      await update<Prompt[] | undefined>(
        draftId,
        (promptQueue) =>
          (promptQueue || []).filter(({ id }) => id !== promptId),
        promptQueueStore,
      );

      // Only update Zustand store after successful IndexedDB update
      removeFromPrompts(promptId);
    } catch (error) {
      console.warn("Failed to remove prompt from queue:", error);
      // Don't update Zustand store if IndexedDB update failed
    }
  });

  // Sequential queue processing - handles automatic processing of queued
  // prompts. Ensures prompts are processed one at a time, only once, at the
  // right moments.
  useEffect(() => {
    if (!processQueue || !firstPrompt || isChatLoading) {
      return;
    }

    // Detect valid state transitions for processing
    const isGenerationJustFinished = !!prevIsGenerating && !isMessageGenerating;
    const isIdle = !prevIsGenerating && !isMessageGenerating;
    const isNewHeadWhileIdle = isIdle && prevFirstPromptId !== firstPrompt.id;

    // Idempotency check - prevent duplicate processing
    const hasNotProcessedThisId = lastProcessedIdRef.current !== firstPrompt.id;

    // Prevent overlapping processing during state flips
    if (isProcessingQueuedRef.current) {
      return;
    }

    // Only process when not generating, transition is correct, and not seen
    if (
      !isMessageGenerating &&
      hasNotProcessedThisId &&
      (isGenerationJustFinished || isNewHeadWhileIdle)
    ) {
      // Hold the gate until generation actually starts to avoid firing through the queue on refresh
      isProcessingQueuedRef.current = true;
      // Mark this prompt as processed now
      lastProcessedIdRef.current = firstPrompt.id;
      sendMessage(firstPrompt.message);
      // Remove from queue after send, keep the processing flag until generation starts
      removeFromPromptQueue(firstPrompt.id).catch((error) => {
        console.error("Failed to remove prompt from queue:", error);
      });
    }
  }, [
    firstPrompt,
    isMessageGenerating,
    isChatLoading,
    prevFirstPromptId,
    prevIsGenerating,
    processQueue,
    removeFromPromptQueue,
    sendMessage,
  ]);

  // When generation starts, release the processing gate so that the next item
  // can be scheduled only after generation finishes (via isGenerationJustFinished)
  useEffect(() => {
    if (isMessageGenerating) {
      isProcessingQueuedRef.current = false;
    }
  }, [isMessageGenerating]);

  return {
    clearPersistedPrompt,
    setPersistedPrompt,
    addToPromptQueue,
    removeFromPromptQueue,
  };
}
